import { render, ComponentType } from 'preact'
import { apiClient } from './api-client'
import { authStore } from './auth-store'
import { generateThemeStyles, ThemeConfig, BrandingConfig } from './theme'
import { baseStyles } from '../styles/base'

export interface WidgetConfig {
  apiKey: string
  baseUrl?: string
}

export interface InitData {
  tenantId: string
  tenantName: string
  /** Sælgerens CVR (jura-hs p44). Mangler i en ældre server. */
  tenantCvr?: string | null
  tenantSlug: string
  locale?: string
  /** ISO 4217 (DKK, EUR, SEK...). Amounts in *Dkk fields are in THIS currency. */
  currency?: string
  /** Studiets IANA-tidszone (tenant.timezone). Mangler i en ældre server → Europe/Copenhagen. */
  timezone?: string
  /** Verified custom domain (no scheme), or null. Used to build outbound links. */
  customDomain?: string | null
  branding: BrandingConfig
  features: {
    seasonClassesEnabled: boolean
    /** Må en besøgende booke en drop-in uden konto? Styrer gæste-grenen i BookingModal. */
    guestCheckoutEnabled?: boolean
    /** OPG-0097: må et udtrykkeligt «Gratis hold» (klass.isFree) bookes uden konto? */
    guestCheckoutFreeClasses?: boolean
    /** Bruger tenanten gæste-kategorier (V335)? Så falder modalens inline gæste-stepper
     *  tilbage til "Book med gæster"-handoffet (stepperen understøtter kun det flade flow). */
    guestCategoriesEnabled?: boolean
    /** Betalingsveje modalerne kan tage inline, MobilePay først (= standardvalget).
     *  Har listen begge, viser modalerne en metode-vælger (PaymentMethodPicker). */
    inlinePaymentMethods?: Array<'mobilepay' | 'stripe'>
    /** "Modtag kontaktforespørgsler". false = /api/widget/contact afviser indsendelsen,
     *  så kontaktformularen skjules. Mangler feltet (ældre server), vises den. */
    contactRequestsEnabled?: boolean
    /** «Vis antal ledige pladser» (OPG-0078). Mangler (ældre server) = exact. */
    spotsDisplay?: 'exact' | 'approximate' | 'full_only'
    /** Deltagerprofiler: hver plads bookes til en profil. BookingModal har ingen
     *  profil-vælger og sender kunden til den native flade. Mangler (ældre server):
     *  serverens PARTICIPANT_PROFILE_REQUIRED giver samme handoff. */
    participantProfilesEnabled?: boolean
  }
  contactInfo?: {
    email: string
    phone: string | null
    address: string | null
    city: string | null
    postalCode: string | null
    mapEmbedUrl: string | null
    openingHours: unknown
  }
  testimonials?: Array<{
    name: string
    subtitle?: string
    text: string
    rating?: number
    image?: string
  }>
  giftCard?: {
    title: string | null
    subtitle: string | null
    bullets: string[]
    /** Studiets eget billede i stedet for 🎁 (OPG-0064). Mangler/null = 🎁. */
    imageUrl?: string | null
  }
  /** Studiets egen tekst på pris-widget'ens «Se hold»-knap (OPG-0064). null = standard. */
  pricing?: {
    buttonText: string | null
    /** Studiets egen prisside-overskrift (V516). Bruges som titel, når embed-koden ikke har `title`.
     *  null/mangler (ældre server) = ingen titel, som før. */
    heading?: string | null
  }
  campaignBanner?: {
    enabled: boolean
    title: string | null
    subtitle: string | null
    buttonText: string | null
    buttonLink: string | null
    backgroundColor: string | null
    backgroundImageUrl: string | null
    textColor: string | null
  }
}

/**
 * Base class for all Holdstyring widgets.
 * Handles Web Component lifecycle, Shadow DOM, theming, and API initialization.
 */
export abstract class HoldstyringWidgetBase extends HTMLElement {
  // HTMLElement types shadowRoot as `ShadowRoot | null` (readonly). We attach
  // an open shadow root in the constructor, so narrow it to non-null here —
  // `declare` keeps it a pure type override (no emitted field, no visibility
  // clash with the base class).
  declare readonly shadowRoot: ShadowRoot
  protected container!: HTMLDivElement
  protected initialized = false
  protected initData: InitData | null = null

  // Theme configuration from attributes
  protected themeConfig: ThemeConfig = {}

  // Abstract method - each widget must implement its own component
  protected abstract getComponent(): ComponentType<{ initData: InitData }>

  // Optional override — return extra props derived from element attributes.
  // The returned object is spread alongside `initData` when rendering the
  // component, giving widgets a way to expose configurability (limit, layout,
  // show-* toggles, etc.) via `data-*` HTML attributes.
  protected getWidgetProps(): Record<string, unknown> {
    return {}
  }

  constructor() {
    super()
    // attachShadow({ mode: 'open' }) sets this.shadowRoot natively.
    this.attachShadow({ mode: 'open' })
  }

  // Observed attributes for theming + base-url + tenant-slug
  static get observedAttributes() {
    return [
      'api-key',
      'base-url',
      'tenant-slug',
      'theme-primary',
      'theme-secondary',
      'theme-accent',
      'theme-font',
      'theme-radius',
      'theme-mode',
      'preview-token',
    ]
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return

    switch (name) {
      case 'theme-primary':
        this.themeConfig.primaryColor = newValue
        break
      case 'theme-secondary':
        this.themeConfig.secondaryColor = newValue
        break
      case 'theme-accent':
        this.themeConfig.accentColor = newValue
        break
      case 'theme-font':
        this.themeConfig.fontFamily = newValue
        break
      case 'theme-radius':
        this.themeConfig.borderRadius = newValue
        break
      case 'theme-mode':
        // Accepterer light/dark/auto; auto følger værtens prefers-color-scheme.
        // Ukendte værdier falder til light, så en tastefejl aldrig giver mørk widget.
        this.themeConfig.mode =
          newValue === 'dark' || newValue === 'auto' ? newValue : 'light'
        break
    }

    // Re-render if already initialized
    if (this.initialized && this.initData) {
      this.updateStyles()
    }
  }

  async connectedCallback() {
    const apiKey = this.getAttribute('api-key')
    // Preview-token (admin widget-bygger live preview) står i stedet for api-key:
    // det er admin-mintet, kortlivet og read-only, og serveren short-circuiter på
    // det før api-key/origin-tjekket. Mindst én af dem skal være til stede.
    const previewToken = this.getAttribute('preview-token') || undefined

    if (!apiKey && !previewToken) {
      this.renderError('An api-key attribute is required.')
      return
    }

    // Determine base URL — explicit base-url attribute > tenant-slug > document host (only on tenant subdomain)
    const baseUrl = this.resolveBaseUrl()
    if (!baseUrl) {
      this.renderError('Could not resolve the Holdstyring host. Add either tenant-slug="your-studio" or base-url="https://..." .')
      return
    }

    // Initialize auth store with API key to scope localStorage keys per tenant
    // (preview er read-only; et fast scope-navn er fint).
    authStore.init(apiKey || 'preview')

    // Initialize API client
    apiClient.init({ baseUrl, apiKey: apiKey || '', previewToken })

    // Show loading state
    this.renderLoading()

    // Initialize widget — uses the shared apiClient.getInitData() so that
    // all <holdstyring-*> custom elements on the same page share a single
    // /api/widget/init round-trip rather than each making their own.
    const result = await apiClient.getInitData<InitData>()

    if (result.error || !result.data) {
      this.renderError(this.actionableInitError(result.code, result.error))
      return
    }

    this.initData = result.data
    this.initialized = true

    // Render the widget
    this.renderWidget()
  }

  disconnectedCallback() {
    // Clean up if needed
    render(null, this.container)
  }

  /**
   * Resolve the API base URL — the host where /api/widget/* endpoints live.
   *
   * Resolution order:
   *   1. Explicit `base-url` attribute (escape hatch for custom domains)
   *   2. `tenant-slug` attribute → https://{slug}.holdstyring.dk
   *   3. If document is loaded on *.holdstyring.dk subdomain, use document.location.host
   *      (so widgets work natively on the tenant's own subdomain)
   *   4. Otherwise null — caller will render an error
   */
  private resolveBaseUrl(): string | null {
    const explicit = this.getAttribute('base-url')
    if (explicit) return explicit.replace(/\/$/, '')

    const slug = this.getAttribute('tenant-slug')
    if (slug && /^[a-z0-9-]+$/.test(slug)) return `https://${slug}.holdstyring.dk`

    // Same-origin embed (widgets running on the tenant's own subdomain)
    if (typeof window !== 'undefined' && window.location?.host) {
      const host = window.location.host
      if (host.endsWith('.holdstyring.dk') && host !== 'widgets.holdstyring.dk') {
        return `${window.location.protocol}//${host}`
      }
    }

    return null
  }

  private renderLoading() {
    this.shadowRoot.innerHTML = `
      <style>
        .yoga-widget-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          font-family: system-ui, sans-serif;
          color: #6b7280;
        }
        .yoga-widget-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid #e5e7eb;
          border-top-color: #10b981;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>
      <div class="yoga-widget-loading">
        <div class="yoga-widget-spinner"></div>
      </div>
    `
  }

  private renderError(message: string) {
    // Create elements safely to prevent XSS
    const style = document.createElement('style')
    style.textContent = `
      .yoga-widget-error {
        padding: 1rem;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 0.5rem;
        color: #dc2626;
        font-family: system-ui, sans-serif;
        font-size: 0.875rem;
      }
    `

    const errorDiv = document.createElement('div')
    errorDiv.className = 'yoga-widget-error'
    errorDiv.textContent = `⚠️ ${message}` // textContent is safe from XSS

    this.shadowRoot.innerHTML = ''
    this.shadowRoot.appendChild(style)
    this.shadowRoot.appendChild(errorDiv)
  }

  /**
   * Turn a widget-API error code into a message that tells the person embedding
   * the widget exactly what to fix — instead of the terse server string.
   * Kept in English on purpose: these are setup errors shown to the site builder
   * (and before the tenant locale is known), like the attribute errors above.
   * Matched on the STABLE `code`, never the text (the text may be localised).
   */
  private actionableInitError(code: string | undefined, serverMessage?: string): string {
    const origin = (typeof window !== 'undefined' && window.location?.origin) || 'your website'
    switch (code) {
      case 'ORIGIN_NOT_ALLOWED':
        return `This website (${origin}) is not on your API key's allow-list, so its requests are blocked. In Holdstyring admin, open Widgets → API keys, click "Edit domains" on your key and add ${origin}.`
      case 'ORIGIN_PENDING_APPROVAL':
        // The domain has asked for access (e.g. via the WordPress plugin) and now
        // waits for the studio — it is never added without the admin's approval.
        return `This website (${origin}) is waiting for the studio's approval. In Holdstyring admin, the studio approves it under Widgets → API keys ("Waiting for your approval"); the widget appears here as soon as it is approved.`
      case 'INVALID_API_KEY':
        return 'Invalid API key. Paste the FULL key (it is only shown once when the key is created — not the shortened "wk_live_…" from the list), and use the same key on every widget on this page.'
      case 'MISSING_API_KEY':
        return 'An api-key attribute is required on the widget tag.'
      case 'WIDGETS_DISABLED':
        // The server message already names the exact switch to flip — keep it.
        return serverMessage || 'Widgets are not enabled for this studio. Turn them on in Holdstyring admin under Settings → Advanced features → Website & content → "Widgets for your website".'
      case 'RATE_LIMITED':
        return 'Too many requests right now. This usually clears on its own within a minute.'
      case 'NETWORK_ERROR':
        return `Could not reach your Holdstyring studio. Check that base-url points to your studio's address (e.g. https://your-studio.class-booking.com) and is spelled correctly.`
      default:
        return serverMessage || 'Could not initialise widget.'
    }
  }

  private updateStyles() {
    const styleElement = this.shadowRoot.querySelector('#yoga-theme-styles')
    if (styleElement && this.initData) {
      styleElement.textContent = generateThemeStyles(this.themeConfig, this.initData.branding)
    }
  }

  private renderWidget() {
    if (!this.initData) return

    // Create style elements
    const baseStyleElement = document.createElement('style')
    baseStyleElement.textContent = baseStyles

    const themeStyleElement = document.createElement('style')
    themeStyleElement.id = 'yoga-theme-styles'
    themeStyleElement.textContent = generateThemeStyles(this.themeConfig, this.initData.branding)

    // Create container with template attribute for CSS-based theming
    this.container = document.createElement('div')
    this.container.className = 'yoga-widget'
    const template = this.initData.branding?.template || 'studio'
    this.container.setAttribute('data-template', template)

    // Build shadow DOM
    this.shadowRoot.innerHTML = ''
    this.shadowRoot.appendChild(baseStyleElement)
    this.shadowRoot.appendChild(themeStyleElement)
    this.shadowRoot.appendChild(this.container)

    // Render Preact component (spread any extra widget props from attributes)
    const Component = this.getComponent() as ComponentType<Record<string, unknown>>
    const extraProps = this.getWidgetProps()
    render(<Component initData={this.initData} {...extraProps} />, this.container)
  }

  // Dispatch custom events that parent page can listen to
  protected dispatchWidgetEvent(eventName: string, detail: unknown = {}) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        composed: true, // Cross shadow DOM boundary
        detail,
      })
    )
  }
}
